/**
 * @file substituteEmailTemplate.ts
 * @description Resolves `{{path.with.dots}}` placeholders against a context
 * object. Used for the org email-template subject/message/signature.
 *
 * Resolution rules:
 * - Path segments are split on `.` and walked into the context. Segments may
 *   contain word characters, dots, and dashes (`[\w.-]+`) — supports kebab-case
 *   field/entity ids like `{{fields.customer-notes.value}}`.
 * - Strings, numbers, and booleans render as their string form.
 * - Anything else (objects, arrays, null/undefined, Timestamps) drops the
 *   placeholder. If a `whitespace + separator + whitespace` joiner (` — `,
 *   ` - `, ` | `, ` : `) immediately precedes the dropped placeholder, that
 *   joiner is dropped along with it. Bare punctuation without surrounding
 *   whitespace (e.g. `prefix-{{x}}`) is *not* dropped — the dash stays.
 * - After substitution, trailing whitespace is trimmed per line.
 *
 * Common paths the consumers populate:
 * - `{{formNumber}}`, `{{formVersionNumber}}`, `{{formType}}`
 * - `{{senderName}}`, `{{senderEmail}}`, `{{senderPhone}}`
 * - `{{entityReferences.<entityId>.entityRecordName}}`
 * - `{{fields.<fieldId>.value}}`
 *
 * The fields / entityReferences shapes here intentionally mirror the read-only
 * subset the resolver walks; callers pass `FormVersion['fields']` and
 * `FormVersion['entityReferences']` which are structurally compatible.
 */

export type EmailTemplateContext = {
    formNumber?: string;
    formVersionNumber?: number;
    formType?: string;
    senderName?: string;
    senderEmail?: string;
    senderPhone?: string;
    fields?: Record<string, { value?: unknown }>;
    entityReferences?: Record<string, { entityRecordName?: string; entityRecordId?: string }>;
};

const PLACEHOLDER_PATTERN = /(\s+[—–\-|:]+\s+)?\{\{([\w.-]+)\}\}/g;

const resolvePath = (root: EmailTemplateContext, path: string): unknown => {
    return path.split('.').reduce<unknown>((acc, key) => {
        if (acc == null || typeof acc !== 'object') return undefined;
        return (acc as Record<string, unknown>)[key];
    }, root);
};

const trimTrailingWhitespacePerLine = (text: string): string =>
    text.split('\n').map((line) => line.replace(/[ \t]+$/, '')).join('\n');

export const substituteEmailTemplate = (
    template: string,
    context: EmailTemplateContext
): string => {
    if (!template) return '';
    const substituted = template.replace(PLACEHOLDER_PATTERN, (_match, sep: string | undefined, path: string) => {
        const value = resolvePath(context, path);
        const rendered =
            typeof value === 'string' ? value :
            typeof value === 'number' || typeof value === 'boolean' ? String(value) :
            null;
        if (rendered === null) return '';
        return (sep ?? '') + rendered;
    });
    return trimTrailingWhitespacePerLine(substituted);
};
